import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./ui/card";
import { Badge } from "./ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { cn } from "@/lib/utils";
import { Thermometer, Bell } from "lucide-react";
import { Link, useParams } from "react-router";
import PageHeader from "./PageHeader";
import useFirebaseKiln from "../hooks/useFirebaseKiln";
import useBartlett from "../hooks/useBartlett";
import useInterval from "../hooks/useInterval";
import { kilnLogo, controllerLogo } from "../helpers/logoHelpers";
import useFirebasePreferences from "../hooks/useFirebasePreferences";
import { convertTemperature, degreeText } from "../helpers/unitHelpers";
import { prettyTimeSince } from "../helpers/dates";

const KilnShowPage = () => {
  const { id } = useParams();
  const kiln = useFirebaseKiln(id!);

  const [dateStamp, setDateStamp] = useState<number>(Date.now());
  const bartlett = useBartlett(
    kiln?.controller_serial ?? "",
    kiln?.controller_username ?? "",
    kiln?.controller_password ?? "",
    dateStamp
  );
  useInterval(() => {
    setDateStamp(Date.now());
  }, 60000);

  if (!kiln) {
    return (
      <div className="space-y-6">
        <PageHeader title="Kiln" breadcrumbs={[{ label: "Kilns", to: "/kilns" }, { label: "Loading..." }]} />
        <Card><CardHeader><div className="h-6 w-1/3 bg-muted animate-pulse rounded" /></CardHeader></Card>
      </div>
    );
  }
  const bartlettMode = bartlett?.status?.mode;
  const cardColour =
    bartlettMode === "Firing" ? "red" : bartlettMode === "Complete" ? "green" : "grey";

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader
        title={kiln.name}
        breadcrumbs={[{ label: "Kilns", to: "/kilns" }, { label: kiln.name }]}
        actions={
          <div className="flex gap-2">
            <Link to={`/kilns/edit/${id}`}><Button variant="outline">Edit</Button></Link>
            <Link to={`/kilns/delete/${id}`}><Button variant="outline">Delete</Button></Link>
          </div>
        }
      />
      <Card className={cn("w-full", cardColour === "red" ? "border-red-500 border-2" : cardColour === "green" ? "border-green-500 border-2" : "border-gray-300 border-2")}>
        <CardHeader><CardTitle>{kiln.name}</CardTitle></CardHeader>
        <CardContent>
          <img className="max-w-full" src={kilnLogo(kiln.manufacturer)} alt="" />
        </CardContent>
        <CardFooter className="flex-col items-start">
          {kiln.controller === "bartlett_genesis" && (
            <>
              <img className="max-w-[300px]" src={controllerLogo(kiln.controller)} alt="" />
              <BartlettGenesisStatus kiln={kiln} bartlett={bartlett} />
            </>
          )}
        </CardFooter>
      </Card>

    </div>
  );
};

const BartlettGenesisStatus = ({ kiln, bartlett }: { kiln: any; bartlett: any }) => {
  const preferences = useFirebasePreferences();
  const [timeNow, setTimeNow] = useState<number>(Date.now());

  useInterval(() => {
    setTimeNow(Date.now());
  }, 1000);

  const reIsATemperature = /^t(\d+)$/;
  const temperature_keys =
    bartlett?.status && Object.keys(bartlett.status).filter((key) => reIsATemperature.test(key));
  return (
    <div>
      <br />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex gap-4">
            {temperature_keys &&
              temperature_keys.map((key: string) => (
                <div key={key} className="text-center">
                  {key}
                  <br />
                  <Badge variant="secondary">
                    <Thermometer className="mr-1 inline-block h-4 w-4" />
                    {convertTemperature(
                      "fahrenheit",
                      preferences?.degrees ?? "celsius",
                      bartlett?.status[key]
                    )}
                    {degreeText(preferences?.degrees)}
                  </Badge>
                </div>
              ))}
          </div>
        </div>
        <div className="text-right">
          <Bell
            className={`h-8 w-8 mt-5 ${bartlett?.status?.alarm === "OFF" ? "text-gray-400" : "text-red-600"}`}
          />
        </div>
      </div>
      <p />
      Program: {bartlett?.program?.name}
      <br />
      Mode: {bartlett?.status?.mode}
      <br />
      Step: {bartlett?.status?.firing?.step}
      <br />
      Set Point: {bartlett?.status?.firing?.set_pt}
      <br />
      Last Updated: {prettyTimeSince(bartlett?.updatedAt)}
      <Accordion type="single" collapsible>
        <AccordionItem value="debug">
          <AccordionTrigger>Debug Data</AccordionTrigger>
          <AccordionContent>
            <pre>{JSON.stringify(bartlett, undefined, 2)}</pre>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default KilnShowPage;
